class ColorSwatchOptions extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Wait for the next tick to ensure children are rendered
    setTimeout(() => {
      this.swatches = this.querySelectorAll('.plp-mapped-colors__swatch');
      this.productCard = this.closest('.card-wrapper');
      this.init();
    }, 0);
  }

  init() {
    const isDesktop = window.matchMedia('(hover: hover) and (min-width: 750px)').matches;

    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));

      if (isDesktop) {
        swatch.addEventListener('mouseenter', this.handleSwatchHover.bind(this));
        swatch.addEventListener('mouseleave', this.handleSwatchHoverEnd.bind(this));
      }
    });
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    if (swatch.disabled) return;

    // Update current selection
    this.updateSelection(swatch);

    // Update product card content using data attributes
    this.updateProductCardFromData(swatch);

    // Update variant options (sizes)
    this.updateVariantOptionsFromData(swatch);
  }

  handleSwatchHover(event) {
    const swatch = event.currentTarget;
    if (swatch.disabled) return;
    this.updateImagesFromData(swatch);
  }

  handleSwatchHoverEnd(event) {
    const selectedSwatch = this.querySelector('.plp-mapped-colors__swatch.is-selected');
    if (selectedSwatch && event.currentTarget !== selectedSwatch) {
      this.updateImagesFromData(selectedSwatch);
    }
  }

  updateSelection(selectedSwatch) {
    // Remove is-selected from all swatches
    this.swatches.forEach(swatch => {
      swatch.classList.remove('is-selected');
      swatch.disabled = false;
    });

    // Add is-selected to clicked swatch
    selectedSwatch.classList.add('is-selected');
    selectedSwatch.disabled = true;
  }

  updateProductCardFromData(swatch) {
    if (!this.productCard) return;

    // Update title href if provided
    const productUrl = swatch.getAttribute('data-product-url');
    if (productUrl) {
      // Update ALL product card links, not just the first one
      const titleLinks = this.productCard.querySelectorAll('.product-card-link, .quickshop__option-variant-link');
      titleLinks.forEach(link => {
        link.href = productUrl;
      });
    }

    // Update images
    this.updateImagesFromData(swatch);

    // Update price
    this.updatePriceFromData(swatch);
  }

  updateVariantOptionsFromData(swatch) {
    if (!this.productCard) return;

    const container = this.productCard.querySelector('.product__variant-options');
    if (!container) return;

    const raw = swatch.getAttribute('data-variants');
    if (!raw) return;

    let variants;
    try {
      variants = JSON.parse(raw);
    } catch (err) {
      try {
        variants = JSON.parse(raw.replace(/&quot;/g, '"'));
      } catch (e) {
        return;
      }
    }

    if (!Array.isArray(variants) || variants.length === 0) return;

    const productUrl = swatch.getAttribute('data-product-url') || '';

    const html = variants
      .map(variant => {
        const classes = [
          'option-value-wrapper',
          'quickshop__option-variant-link',
          'product__option-input-wrapper'
        ];
        if (!variant.available) classes.push('is-disabled');

        const size = (variant.title || '').toString();
        const href = productUrl ? `${productUrl}?variant=${variant.id}` : '#';
        const ariaDisabled = variant.available ? '' : ' aria-disabled="true"';
        return `<a href="${href}" class="${classes.join(' ')}" data-size-link data-size="${this.escapeHtml(size)}"${ariaDisabled}><span class="option-value" aria-hidden="true">${this.escapeHtml(size)}</span></a>`;
      })
      .join('');

    container.innerHTML = html;
  }

  updateMediaElement(el, swatch, prefix) {
    if (!el) return;
    const src = swatch.getAttribute(`data-${prefix}-src`);
    const srcset = swatch.getAttribute(`data-${prefix}-srcset`);
    const alt = swatch.getAttribute(`data-${prefix}-alt`);
    const width = swatch.getAttribute(`data-${prefix}-width`);
    const height = swatch.getAttribute(`data-${prefix}-height`);
    if (src) el.src = src;
    if (srcset) el.srcset = srcset;
    if (alt) el.alt = alt;
    if (width) el.width = parseInt(width, 10);
    if (height) el.height = parseInt(height, 10);
  }

  updateImagesFromData(swatch) {
    const mediaContainer = this.productCard.querySelector('.card__media .media');
    if (!mediaContainer) return;

    const videoSources = swatch.getAttribute('data-featured-video-sources');

    if (videoSources) {
      // New product has video as primary — create or update video element
      let video = mediaContainer.querySelector('video.card__media-item');
      if (!video) {
        // Replace existing primary img with video
        const existingImg = mediaContainer.querySelector('img.card__media-item');
        video = document.createElement('video');
        video.className = 'card__video js-card-video-primary card__media-item';
        video.autoplay = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        if (existingImg) existingImg.replaceWith(video);
        else mediaContainer.prepend(video);
      }
      // Update sources
      video.innerHTML = '';
      videoSources.split(',').forEach(sourceStr => {
        const [url, type] = sourceStr.split('|');
        const source = document.createElement('source');
        source.src = url;
        source.type = type;
        video.appendChild(source);
      });
      video.load();
      video.play().catch(() => {});

      // Update secondary hover img if present
      const secondaryImg = mediaContainer.querySelectorAll('img.card__media-item')[0];
      this.updateMediaElement(secondaryImg, swatch, 'secondary');
    } else {
      // New product has image as primary — create or update an img element
      const existingVideo = mediaContainer.querySelector('video.card__media-item');
      if (existingVideo) existingVideo.remove();

      let img = mediaContainer.querySelector('img.card__media-item');
      if (!img) {
        img = document.createElement('img');
        img.className = 'motion-reduce card__media-item';
        mediaContainer.prepend(img);
      }
      this.updateMediaElement(img, swatch, 'featured');

      // Ensure secondary hover img exists for hover effect
      let secondaryHoverImg = mediaContainer.querySelectorAll('img.card__media-item')[1];
      if (!secondaryHoverImg) {
        secondaryHoverImg = document.createElement('img');
        secondaryHoverImg.className = 'motion-reduce card__media-item';
        mediaContainer.appendChild(secondaryHoverImg);
      }
      this.updateMediaElement(secondaryHoverImg, swatch, 'secondary');
    }

    // Re-init rollover images for this card after media swap
    if (typeof initRolloverImages === 'function') {
      initRolloverImages();
    }
  }

  updatePriceFromData(swatch) {
    const priceElement = this.productCard.querySelector('.price');
    if (!priceElement) return;

    const priceCents = parseInt(swatch.getAttribute('data-price') || '0', 10);
    const compareAtCents = parseInt(swatch.getAttribute('data-compare-at-price') || '0', 10);

    const priceRegular = priceElement.querySelector('.price-item--regular');
    const priceSale = priceElement.querySelector('.price-item--sale');

    if (compareAtCents > priceCents) {
      priceElement.classList.add('price--on-sale');
      if (priceSale) priceSale.textContent = this.formatMoney(priceCents);
      if (priceRegular) priceRegular.textContent = this.formatMoney(compareAtCents);
    } else {
      priceElement.classList.remove('price--on-sale');
      if (priceRegular) priceRegular.textContent = this.formatMoney(priceCents);
      if (priceSale) priceSale.textContent = '';
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available, otherwise fallback
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    const currency = window.Shopify.currency.active;

    return (cents / 100).toFixed(0) + ' ' + currency;
  }

  escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

customElements.define('plp-mapped-product-colors', ColorSwatchOptions);
